import { ApiResult } from '../types/api/common';
import { PrintDocument, PrintDocumentResponse } from '../types/api/responses';

/**
 * Номер полиса, пригодный для имени файла: серия-номер ОСАГО (ХХХ0163314937)
 * или номер договора каско (272-0004534-00000). Внутренние идентификаторы —
 * uuid и MOCK-* — в имя файла не попадают.
 */
export const getPolicyNumber = (
  ...candidates: Array<string | undefined | null>
): string | undefined =>
  candidates
    .map((candidate) => candidate?.trim())
    .find((candidate) => !!candidate && /^([A-ZА-Я]{3}\d+|\d[\d-]+\d)$/i.test(candidate));

/**
 * Имя файла полиса: Полис_ОСАГО_<номер>.pdf, без номера — Полис_ОСАГО.pdf.
 * index различает файлы одного полиса: Полис_Ипотека_1.pdf, Полис_Ипотека_2.pdf.
 */
export const getDocumentName = (
  product: string,
  policyNumber?: string,
  { extension = 'pdf', index }: { extension?: string; index?: number } = {},
): string => {
  const parts = ['Полис', product.replace(/\s+/g, '_'), policyNumber, index].filter(
    (part) => part !== undefined && part !== '',
  );
  return `${parts.join('_')}.${extension}`;
};

const hasSignature = (content: string | undefined, signature: string): boolean => {
  if (!content) return false;
  try {
    return atob(content.slice(0, 8)).startsWith(signature);
  } catch {
    return false;
  }
};

/** Пришедший base64 действительно декодируется и является PDF */
export const isPdfContent = (content?: string): boolean => hasSignature(content, '%PDF');

const POLICY_FILE_FORMATS: Record<string, { extension: string; signature: string }> = {
  'application/pdf': { extension: 'pdf', signature: '%PDF' },
  'application/zip': { extension: 'zip', signature: 'PK' },
};

export type PolicyFile = { document: PrintDocument; mime: string; extension: string };

export type PolicyDocumentResult =
  | { status: 'ok'; document: PrintDocument }
  | { status: 'requestFailed' | 'noDocuments' | 'notPdf' };

export type PolicyFilesResult =
  | { status: 'ok'; files: PolicyFile[] }
  | { status: 'requestFailed' | 'noDocuments' | 'unsupported' };

/**
 * Документ полиса из ответа печати. Берётся только documents выбранной карточки:
 * crossProducts относятся к другим договорам и скачиваться не должны.
 */
export const selectPolicyDocument = ([err, res]: ApiResult<PrintDocumentResponse>): PolicyDocumentResult => {
  if (err || !res || res.error) return { status: 'requestFailed' };

  const documents = res.data?.documents || [];
  if (documents.length === 0) return { status: 'noDocuments' };

  const document = documents.find((doc) => isPdfContent(doc.content));
  if (!document) return { status: 'notPdf' };

  return { status: 'ok', document };
};

/**
 * Все файлы полиса ипотеки: комплекс «Ак Барс» — два PDF разных полисов,
 * комплекс МАКС — один zip. Формат берётся из type и сверяется с содержимым.
 */
export const selectPolicyFiles = ([err, res]: ApiResult<PrintDocumentResponse>): PolicyFilesResult => {
  if (err || !res || res.error) return { status: 'requestFailed' };

  const documents = res.data?.documents || [];
  if (documents.length === 0) return { status: 'noDocuments' };

  const files = documents.flatMap((document): PolicyFile[] => {
    const mime = (document.type || '').toLowerCase();
    const format = POLICY_FILE_FORMATS[mime];
    return format && hasSignature(document.content, format.signature)
      ? [{ document, mime, extension: format.extension }]
      : [];
  });
  if (files.length === 0) return { status: 'unsupported' };

  return { status: 'ok', files };
};
